import color from 'color';
import React, { useEffect, useState } from 'react';
import { Animated, TouchableOpacity, View, ViewStyle } from 'react-native';
import { Divider, Menu } from 'react-native-paper';

import styles from './blank-picker.style';
import { useColors } from '../../hooks';
import {
  correctAnimation,
  incorrectAnimation,
} from '../choice-bubble/choice-bubble.animations';
import ThemedText2 from '../themed-text-2';

interface BlankPickerProps {
  index: number | string;
  options?: string[];
  word?: string;
  isResultVisible?: boolean;
  isResultFinal?: boolean;
  setResult: (index, result) => void;
  isCorrect: boolean;
  isActive?: boolean;
  isDisabled?: boolean;
  fontSize?: number;
  containerStyle?: ViewStyle;
}

export function BlankPicker({
  index,
  options = [],
  word,
  isResultVisible = false,
  isResultFinal = false,
  setResult,
  isCorrect,
  isActive = false,
  isDisabled = false,
  fontSize = 24,
  containerStyle = undefined,
}: BlankPickerProps) {
  const colors = useColors();
  const [isMenuVisible, setMenuVisibility] = useState(false);
  const [x] = useState(new Animated.Value(0));
  const [s] = useState(new Animated.Value(0));

  useEffect(() => {
    if (!isResultVisible) {
      return;
    }

    if (!isCorrect) {
      incorrectAnimation(x).start();
    } else {
      correctAnimation(s).start();
    }
  }, [isResultVisible, isCorrect, x, s]);

  const answerAndHide = (option) => {
    setResult(index, option);
    setMenuVisibility(false);
  };

  const errorStyle = !isCorrect &&
    isResultVisible && {
      backgroundColor: color(colors.error).alpha(0.1).string(),
      borderColor: colors.error,
    };

  const activeStyle = isActive && {
    backgroundColor: color(colors.primary).alpha(0.1).string(),
  };

  if (isResultFinal) {
    return (
      <View style={containerStyle}>
        <ThemedText2 style={[styles.finalText, { fontSize }]} type="primary">
          {word}
        </ThemedText2>
      </View>
    );
  }

  const menuOptions = options?.map(
    (option, arrIndex) =>
      (
        <View key={`${index}-${arrIndex}`}>
          <Menu.Item onPress={() => answerAndHide(option)} title={option} />
          {arrIndex < options.length - 1 && <Divider />}
        </View>
      ) ?? []
  );

  return (
    <Menu
      visible={isMenuVisible}
      onDismiss={() => setMenuVisibility(false)}
      contentStyle={[styles.menuContent, { borderColor: colors.darkGray }]}
      anchor={
        <TouchableOpacity
          accessibilityRole="button"
          style={containerStyle}
          onPress={() => setMenuVisibility(true)}
          disabled={isDisabled}
        >
          {!word ? (
            <View
              style={[
                styles.blank,
                {
                  backgroundColor: colors.grayLight,
                  borderColor: colors.lightSilverGray,
                },
                isMenuVisible && {
                  borderColor: colors.primary,
                  backgroundColor: colors.primaryLight,
                },
                activeStyle,
                errorStyle,
              ]}
            />
          ) : (
            <View
              style={[
                styles.nonEmptyBlank,
                { borderColor: colors.borderColor },
              ]}
            >
              <ThemedText2 type="gray" style={styles.nonEmptyBlankText}>
                {word}
              </ThemedText2>
            </View>
          )}
        </TouchableOpacity>
      }
    >
      {menuOptions}
    </Menu>
  );
}
